import type { RollWidgetAnchor } from "@/components/roll-widgets/roll-widget-slots";
import type { VideoSubject } from "@/lib/live/video/video-types";

/** Où une caméra de replay se pose, et ce qu'elle réserve. */
export type CameraPlacement = {
  readonly anchor: RollWidgetAnchor;
  /** Repliée en bulle tant que personne n'en a décidé autrement. */
  readonly collapsed: boolean;
  /**
   * Elle occupe le pied du rouleau : la tête de lecture remonte au-dessus
   * d'elle au lieu de passer derrière (`Studio.setFootReserve`).
   */
  readonly foot: boolean;
};

/**
 * Où va chaque caméra d'un replay.
 *
 * # Les mains prennent le pied du rouleau
 *
 * Debout, la caméra des mains se pose **entre le clavier témoin et la tête de
 * lecture**, qui remonte pour lui laisser la place. C'est la continuité qui le
 * justifie : la note tombe, franchit la ligne d'attaque, atteint le clavier du
 * rouleau, et les vraies mains la jouent juste en dessous. Une seule scène,
 * lue de haut en bas, au lieu d'une vignette posée par-dessus la musique.
 *
 * Le prix est réel et connu : la bande est prélevée sur la fenêtre visible,
 * donc sur l'anticipation. C'est exactement ce qu'on achète — sur un replay on
 * ne joue pas, on regarde quelqu'un jouer.
 *
 * # Un visage ne continue rien
 *
 * Posé là, il serait une vignette collée au mauvais endroit. Il part donc en
 * bulle dans le coin haut-gauche : on sait qui est là, on l'ouvre si on veut
 * le voir, et le rouleau reste entier.
 *
 * # Rien n'est inconnu
 *
 * Ce qu'une caméra filme se lit dans la séance, et une caméra dont la séance
 * n'a rien dit filme des mains (`recordedSubject`). Il y eut un troisième cas,
 * « on ne sait pas », pour les pistes d'avant le nommage des caméras : il
 * gardait la vignette ouverte en haut. Il n'a plus lieu d'être — faute de
 * mieux, des mains, qui gardent elles aussi leur image ouverte, et
 * l'interprète corrige d'un geste ce que la séance n'a pas su dire.
 *
 * # Au bureau, pareil
 *
 * La même scène, pour la même raison. La vignette y passait par-dessus la
 * tête de lecture, dans le coin bas-gauche (rapporté le 2026-09-22). Le visage
 * y reste ouvert en haut à gauche : le rouleau est assez grand pour lui.
 *
 * # Téléphone couché, rien de tout cela
 *
 * Le rouleau n'a que 258 px : une bande pleine largeur en mangerait les trois
 * quarts, et c'est lui qu'on est venu regarder. Les caméras y restent des
 * bulles dans le coin que ni le rail de minimap ni la barre ne disputent.
 *
 * Module pur : ni React, ni DOM. C'est une décision, elle s'éprouve.
 */
export function placeCamera(
  subject: VideoSubject,
  screen: { readonly handheld: boolean; readonly standing: boolean },
): CameraPlacement {
  if (screen.handheld && !screen.standing) {
    return { anchor: "bottom-left", collapsed: true, foot: false };
  }

  if (subject === "hands") {
    return { anchor: "bottom-left", collapsed: false, foot: true };
  }

  return { anchor: "top-left", collapsed: screen.standing, foot: false };
}

/**
 * Place toutes les caméras d'un replay, la liste étant ce qui compte.
 *
 * # Debout, une seule image reste ouverte
 *
 * Le rouleau fait 346 px de large sur un téléphone : deux vignettes ouvertes
 * n'y tiennent pas côte à côte. Elles s'y entassaient pourtant — deux
 * téléphones peuvent filmer les mains, rien ne l'interdit, et chacune se
 * croyait seule à prétendre au pied du rouleau. La rangée débordait alors par
 * la gauche : une image au milieu, l'autre coupée au bord, et la bande
 * réservée pour celle qui avait mesuré en dernier.
 *
 * Celle qui reste ouverte est **celle du pied** — les mains, la scène qu'on
 * est venu regarder. Toutes les autres partent en bulle dans le coin
 * haut-gauche : on sait qui est là, on l'ouvre si on veut la voir, et le
 * rouleau reste entier. Des visages seuls restent donc tous en bulle.
 *
 * # Au bureau, un pied de chaque côté
 *
 * Le rouleau y est assez large pour deux images des mains : la première prend
 * le coin bas-gauche, la deuxième le coin bas-droit, et la tête de lecture
 * remonte au-dessus de la plus haute. Une troisième — le récital n'en publie
 * que deux, mais un replay ne se refuse pas — rejoint le visage en haut.
 *
 * # Téléphone couché, rien à arbitrer
 *
 * Tout y est déjà une bulle, et deux bulles tiennent dans la rangée.
 */
export function placeCameras(
  subjects: readonly VideoSubject[],
  screen: { readonly handheld: boolean; readonly standing: boolean },
): readonly CameraPlacement[] {
  const placements = subjects.map((subject) => placeCamera(subject, screen));

  if (screen.handheld && !screen.standing) {
    return placements;
  }

  // Les pieds disponibles, dans l'ordre où les caméras les prennent.
  const feet: RollWidgetAnchor[] = screen.standing ? ["bottom-left"] : ["bottom-left", "bottom-right"];

  return placements.map((placement) => {
    if (!placement.foot) {
      return placement;
    }

    const anchor = feet.shift();

    if (anchor === undefined) {
      return { anchor: "top-left", collapsed: screen.standing, foot: false };
    }

    return { ...placement, anchor };
  });
}
